import { makeStyles } from '@material-ui/styles'
import { RootState, useAppSelector } from 'app/store'
import StepperModalForm, { ModalStep } from 'core/elements/modal/StepperModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import { createKaapiBYOClusterBody } from 'k8s/components/kaapi/byo-clusters/action-helpers'
import { createKaapiBYOCluster } from 'k8s/components/kaapi/byo-clusters/actions'
import { createBYOMachineTemplateBody } from 'k8s/components/kaapi/byo-machine-template/action-helpers'
import { createKaapiBYOMachineTemplate } from 'k8s/components/kaapi/byo-machine-template/actions'
import { createKaapiClusterAddonBody } from 'k8s/components/kaapi/cluster-addons/action-helpers'
import { createKaapiClusterAddon } from 'k8s/components/kaapi/cluster-addons/actions'
import { createKaapiClusterBody } from 'k8s/components/kaapi/clusters/action-helpers'
import { createKaapiCluster } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersSelector } from 'k8s/components/kaapi/clusters/selectors'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import { createHostedControlPlaneBody } from 'k8s/components/kaapi/hosted-control-plane/action-helpers'
import { createKaapiHostedControlPlane } from 'k8s/components/kaapi/hosted-control-plane/actions'
import { createKaapiK8sInstallerConfigTemplateBody } from 'k8s/components/kaapi/k8s-installer-config-templates/action-helpers'
import { createKaapiK8sInstallerConfigTemplate } from 'k8s/components/kaapi/k8s-installer-config-templates/actions'
import { createKaapiKubeadmConfigTemplateBody } from 'k8s/components/kaapi/kubeadm-config-templates/action-helpers'
import { createKaapiKubeadmConfigTemplate } from 'k8s/components/kaapi/kubeadm-config-templates/actions'
import { createKaapiMachineDeploymentBody } from 'k8s/components/kaapi/machine-deployment/action-helpers'
import { createKaapiMachineDeployment } from 'k8s/components/kaapi/machine-deployment/actions'
import { addClusterLabelToByoHost, listByoHosts } from 'k8s/components/nodes/byoh/actions'
import ValidatedForm from 'pf9-ui-components/built/components/validatedForm/ValidatedForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import ClusterArchitectureFieldsBYO from './form-components/ClusterArchitectureFieldsBYO'
import ClusterConfigFields from './form-components/ClusterConfigFields'
import ClusterNameField from './form-components/ClusterNameField'
import { getDUInternalIP } from './form-components/helpers'
import { isAirgapped } from 'core/utils/helpers'

export const UNSUPPORTED_VM_FLAVORS = ['pf9.unknown', 'm1.tiny', 'm1.small']

const initialValues = {
  physicalNodes: [],
  calico: true,
  coreDns: true,
  enableAddons: true,
  controlPlaneType: 'managed',
  apiServerFlags: '',
  schedulerFlags: '',
  controllerManagerFlags: '',
  metallb: false,
}

/**
 * @summary - This component is used to deploy a k8s workload cluster with Physical nodes (BYOH)
 * @flow
 * 1. Cluster Infrastructure:
 *    - User selects BYO from the radio group
 * 2. Cluster Architecture:
 *    - PhysicalNodesTable will be rendered - User can add nodes to the cluster
 *    - User selects the control plane type and its configuration
 * 3. Cluster Configuration: (Same as virtualized cluster)
 * 4. Final Touches -
 *    - Cluster Name: User enters the cluster name
 * 5. User clicks the "Add Cluster" button - handleBYOClusterCreation will be called
 *    - Validate the fields - physical nodes should be selected
 *    - We have added error handling after each API call
 *    - Create Cluster object
 *    For Each Physical Node
 *      - Update the host labels to include cluster name
 *    - Create HostedControlPlane object
 *    - Create BYOCluster object
 *    - Create KubeadmConfigTemplate object
 *    - Create BYOMachineTemplate object
 *    - Create MachineDeployment object
 *    - Create ClusterAddons object
 *    - Create K8sInstaller object
 * 6.On success, the user will be redirected to the cluster detail page
 */
const ClusterDeploymentForm = ({ onClose }) => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [physicalNodeErrors, setPhysicalNodeErrors] = useState<{
    title?: string
    message?: string
  }>(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const isAirgappedEnv = isAirgapped()

  // Load Clusters
  // const { loading: loadingClusters } = useListAction(listKaapiClusters)
  const clusters = useAppSelector(kaapiClustersSelector)

  // Physical Nodes
  const { reload: reloadPhysicalNodes } = useListAction(listByoHosts)

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const duInternalIP = getDUInternalIP(configMaps)

  // Create Actions
  const {
    update: createHostedControlPlane,
    updating: creatingHostedControlPlane,
    error: createHostedControlPlaneError,
  } = useUpdateAction(createKaapiHostedControlPlane)
  const {
    update: createCluster,
    updating: creatingCluster,
    error: createClusterError,
  } = useUpdateAction(createKaapiCluster)
  const {
    update: createKubeadmConfigTemplate,
    updating: creatingKubeadmConfigTemplate,
    error: createKubeadmConfigTemplateError,
  } = useUpdateAction(createKaapiKubeadmConfigTemplate)
  const {
    update: createMachineDeployment,
    updating: creatingMachindeDeployment,
    error: createMachineDeploymentError,
  } = useUpdateAction(createKaapiMachineDeployment)
  const {
    update: createClusterAddon,
    updating: creatingClusterAddon,
    error: createClusterAddonError,
  } = useUpdateAction(createKaapiClusterAddon)

  // BYOCluster Actions
  const {
    update: createBYOCluster,
    updating: creatingBYOCluster,
    error: createBYOClusterError,
  } = useUpdateAction(createKaapiBYOCluster)
  const {
    update: createBYOMachineTemplate,
    updating: creatingBYOMachineTemplate,
    error: createBYOMachineTemplateError,
  } = useUpdateAction(createKaapiBYOMachineTemplate)
  const {
    update: createK8sInstallerConfigTemplate,
    updating: creatingK8sInstallerConfigTemplate,
    error: createK8sInstallerConfigTemplateError,
  } = useUpdateAction(createKaapiK8sInstallerConfigTemplate)
  const {
    update: addClusterLabelToByoHostFn,
    updating: addingClusterLabelToByoHost,
    error: addClusterLabelToByoHostError,
  } = useUpdateAction(addClusterLabelToByoHost)

  const hasError =
    !!createHostedControlPlaneError ||
    !!createClusterError ||
    !!createKubeadmConfigTemplateError ||
    !!createMachineDeploymentError ||
    !!createClusterAddonError ||
    !!createBYOClusterError ||
    !!createBYOMachineTemplateError ||
    !!createK8sInstallerConfigTemplateError ||
    !!addClusterLabelToByoHostError

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const error =
      createHostedControlPlaneError ||
      createClusterError ||
      createKubeadmConfigTemplateError ||
      createMachineDeploymentError ||
      createClusterAddonError ||
      createBYOClusterError ||
      createBYOMachineTemplateError ||
      createK8sInstallerConfigTemplateError ||
      addClusterLabelToByoHostError

    setError({
      title: error?.title || 'Error creating cluster',
      message: error?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [
    hasError,
    createHostedControlPlaneError,
    createClusterError,
    createKubeadmConfigTemplateError,
    createMachineDeploymentError,
    createClusterAddonError,
    createBYOClusterError,
    createBYOMachineTemplateError,
    createK8sInstallerConfigTemplateError,
    addClusterLabelToByoHostError,
  ])

  // Handle BYO cluster creation
  const handleBYOClusterCreation = async () => {
    setError(null)
    setLoading(true)
    setPhysicalNodeErrors(null)

    // Check if physical nodes are provided
    if (params?.physicalNodes?.length === 0) {
      setPhysicalNodeErrors({ title: 'Error', message: 'Please select atleast one physical node' })
      setLoading(false)
      return
    }

    try {
      // Create Cluster
      const clusterCreateBody = createKaapiClusterBody({
        name: params.clusterName,
        namespace,
        hostedControlPlaneRefName: `${params.clusterName}cp`,
        infrastructureRefName: params.clusterName,
        version: params.k8sVersion,
        isBYOCluster: true,
        gpuAddons: params.gpuAddons,
      })
      const { success: createClusterSuccess, error: createClusterError } = await createCluster({
        namespace,
        body: clusterCreateBody,
      })
      // If cluster creation fails, return
      if (!createClusterSuccess) {
        setLoading(false)
        setError(createClusterError || { title: 'Error', message: 'Failed to create cluster' })
        return
      }

      // Update the selected nodes
      const promises = []
      params?.physicalNodes?.forEach(async (node) => {
        promises.push(
          addClusterLabelToByoHostFn({
            name: node?.name,
            clusterName: params.clusterName,
          }),
        )
      })
      const res = await Promise.all(promises)

      // If any of the promises failed, return
      if (res?.some((item) => !item?.success)) {
        setError({ title: 'Error', message: 'Failed to update labels on physical nodes' })
        setLoading(false)
        return
      }

      // Create BYOCluster
      const byoClusterBody = createKaapiBYOClusterBody({
        name: params.clusterName,
        namespace,
        controlplaneName: `${params.clusterName}cp`,
      })
      const { success: createBYOSuccess, error: createBYOError } = await createBYOCluster({
        namespace,
        body: byoClusterBody,
      })
      // If BYOCluster creation fails, return
      if (!createBYOSuccess) {
        setLoading(false)
        setError(createBYOError || { title: 'Error', message: 'Failed to create BYOCluster' })
        return
      }

      // Create HostedControlPlane
      const hostedControlPlaneCreateBody = createHostedControlPlaneBody({
        name: `${params.clusterName}cp`,
        namespace,
        version: params.k8sVersion,
        apiServerFlags: params.apiServerFlags ? params.apiServerFlags.split(',') : [],
        schedulerFlags: params.schedulerFlags ? params.schedulerFlags.split(',') : [],
        controllerManagerFlags: params.controllerManagerFlags
          ? params.controllerManagerFlags.split(',')
          : [],
        clusterName: params.clusterName,
      })
      const {
        success: createHostedControlPlaneSuccess,
        error: createHostedControlPlaneError,
      } = await createHostedControlPlane({
        namespace,
        body: hostedControlPlaneCreateBody,
      })
      // If HostedControlPlane creation fails, return
      if (!createHostedControlPlaneSuccess) {
        setLoading(false)
        setError(
          createHostedControlPlaneError || {
            title: 'Error',
            message: 'Failed to create HostedControlPlane',
          },
        )
        return
      }

      // Generate a unique name with short UUID
      const name = `${params.clusterName}`

      // Create KubeadmConfigTemplate
      const kubeadmConfigTemplateBody = createKaapiKubeadmConfigTemplateBody({
        name,
        namespace,
        isBYOCluster: true,
        clusterName: params.clusterName,
        duInternalIP,
        isAirgappedEnv,
      })
      // If KubeadmConfigTemplate creation fails, return
      const {
        success: createKubeadmConfigTemplateSuccess,
        error: createKubeadmConfigTemplateError,
      } = await createKubeadmConfigTemplate({ namespace, body: kubeadmConfigTemplateBody })
      if (!createKubeadmConfigTemplateSuccess) {
        setLoading(false)
        setError(
          createKubeadmConfigTemplateError || {
            title: 'Error',
            message: 'Failed to create KubeadmConfigTemplate',
          },
        )
        return
      }

      // Create ByoMachineTemplate
      const byoMachineTemplateBody = createBYOMachineTemplateBody({
        name,
        namespace,
        k8sInstallerConfigTemplateRefName: name,
        clusterName: params.clusterName,
      })
      // If ByoMachineTemplate creation fails, return
      const {
        success: createBYOMachineTemplateSuccess,
        error: createBYOMachineTemplateError,
      } = await createBYOMachineTemplate({ namespace, body: byoMachineTemplateBody })
      if (!createBYOMachineTemplateSuccess) {
        setLoading(false)
        setError(
          createBYOMachineTemplateError || {
            title: 'Error',
            message: 'Failed to create ByoMachineTemplate',
          },
        )
        return
      }

      // Create MachineDeployment
      const machineDeploymentCreateBody = createKaapiMachineDeploymentBody({
        name,
        namespace,
        clusterName: params.clusterName,
        replicas: params.physicalNodes.length,
        configTemplateRefName: name,
        infrastructureRefName: name,
        k8sVersion: params.k8sVersion,
        openstackCluster: params.openstackCluster,
        isBYOCluster: true,
      })
      // If MachineDeployment creation fails, return
      const {
        success: createMachineDeploymentSuccess,
        error: createMachineDeploymentError,
      } = await createMachineDeployment({ namespace, body: machineDeploymentCreateBody })
      if (!createMachineDeploymentSuccess) {
        setLoading(false)
        setError(
          createMachineDeploymentError || {
            title: 'Error',
            message: 'Failed to create MachineDeployment',
          },
        )
        return
      }

      // Create ClusterAddon
      if (params.metallb) {
        const metallbBody = createKaapiClusterAddonBody({
          clusterName: params.clusterName,
          namespace,
          addonType: 'metallb',
          version: '0.14.9',
        })
        const {
          success: createClusterAddonSuccess,
          error: createClusterAddonError,
        } = await createClusterAddon({ namespace, body: metallbBody })
        if (!createClusterAddonSuccess) {
          setLoading(false)
          setError(
            createClusterAddonError || {
              title: 'Error',
              message: 'Failed to create ClusterAddon',
            },
          )
          return
        }
      }

      // Create K8sInstallerConfigTemplate
      const k8sInstallerConfigTemplateBody = createKaapiK8sInstallerConfigTemplateBody({
        name,
        namespace,
      })
      const {
        success: createK8sInstallerConfigTemplateSuccess,
        error: createK8sInstallerConfigTemplateError,
      } = await createK8sInstallerConfigTemplate({
        namespace,
        body: k8sInstallerConfigTemplateBody,
      })
      if (!createK8sInstallerConfigTemplateSuccess) {
        setLoading(false)
        setError(
          createK8sInstallerConfigTemplateError || {
            title: 'Error',
            message: 'Failed to create K8sInstallerConfigTemplate',
          },
        )
        return
      }

      if (!hasError) {
        reloadPhysicalNodes() // Refresh Physical Nodes
        updateK8sPluginGlobalParams({ cluster: params.clusterName })
        onClose()
        history.push(
          routeManager.routes.kubernetes.infrastructure.cluster.path({
            cluster: params.clusterName,
            tab: IClusterDetailTabs.OVERVIEW,
          }),
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const submitting =
    creatingHostedControlPlane ||
    creatingCluster ||
    creatingKubeadmConfigTemplate ||
    creatingMachindeDeployment ||
    creatingClusterAddon ||
    creatingBYOCluster ||
    creatingBYOMachineTemplate ||
    creatingK8sInstallerConfigTemplate ||
    addingClusterLabelToByoHost ||
    loading

  const steps: ModalStep[] = [
    {
      stepId: 1,
      headerTitle: 'Cluster Architecture',
      description:
        'Select the compute infrastructure for this cluster. Using Physical Servers provides direct access to bare metal resources, offering maximum performance and isolation for specialized or high-throughput workloads.',
      component: (
        <div className={classes.formSection}>
          <ClusterNameField
            params={params}
            getParamsUpdater={getParamsUpdater}
            clusters={clusters}
          />
          <ClusterArchitectureFieldsBYO
            params={params}
            getParamsUpdater={getParamsUpdater}
            physicalNodeErrors={physicalNodeErrors}
            setError={setError}
          />
        </div>
      ),
      stepValidator: () => {
        if (isAirgappedEnv && !duInternalIP) {
          setError({
            title: 'Error',
            message:
              'Something went wrong, please try again. Tenant may not be configured correctly',
          })
          return {
            success: false,
          }
        }
        if (!params?.physicalNodes?.length) {
          setPhysicalNodeErrors({
            title: 'Error',
            message: 'Please select atleast one physical node',
          })
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
    {
      stepId: 2,
      headerTitle: 'Configure your Cluster',
      description:
        'Configure your cluster’s Kubernetes version, your GPUs partitioning strategy (if applicable), optional Kubernetes flags, and add-ons.',
      component: (
        <div className={classes.formSection}>
          <ClusterConfigFields params={params} getParamsUpdater={getParamsUpdater} isBYOCluster />
        </div>
      ),
    },
  ]

  return (
    <StepperModalForm
      steps={steps}
      title="Deploy A New Cluster"
      maxWidth={876}
      open={true}
      onClose={onClose}
      onSubmit={handleBYOClusterCreation}
      error={error}
      resetError={() => setError(null)}
      submitting={submitting}
      ValidatedFormComponent={ValidatedForm as any}
      params={params}
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
  },
}))

export default ClusterDeploymentForm
